<template>
  <div class="schedule-calendar-page container-fluid p-4">
    <div class="page-header">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h3 class="mb-0 fw-bold">Schedule Calendar</h3>
          <small class="text-muted">View and manage inspection schedules</small>
        </div>
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <div class="status-legend">
            <span v-for="(color, key) in statusColors" :key="key" class="legend-item">
              <span class="legend-dot" :style="{ background: color }"></span>
              {{ formatStatus(key) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="calendar-container">
      <!-- Loading Overlay -->
      <div v-if="loading" class="calendar-loading">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading schedules...</span>
        </div>
      </div>
      <FullCalendar
        ref="calendarRef"
        :options="calendarOptions"
      />
    </div>

    <!-- Day Schedule Modal -->
    <FormModal ref="dayModal" :title="modalTitle" id="day_schedule_modal" size="lg">
      <div class="day-schedule-modal">
        <div v-if="selectedDaySchedules && selectedDaySchedules.length > 0">
          <div class="modal-summary">
            {{ selectedDaySchedules.length }} schedule{{ selectedDaySchedules.length > 1 ? 's' : '' }} on this day
          </div>
          <table class="schedule-table">
            <thead>
              <tr>
                <th class="col-time">Time</th>
                <th class="col-title">Schedule</th>
                <th class="col-status">Status</th>
                <th class="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="schedule in selectedDaySchedules"
                :key="schedule.id"
                class="schedule-row"
              >
                <td class="col-time">
                  <i class="fas fa-clock"></i>
                  {{ formatTime(schedule.schedule_date) }}
                </td>
                <td class="col-title">
                  <div class="schedule-name">{{ schedule.get_parent?.get_work_order?.name || 'Untitled' }}</div>
                  <div class="schedule-id">#{{ schedule.get_parent?.get_work_order?.external_id || schedule.id }}</div>
                </td>
                <td class="col-status">
                  <span v-html="getStatusBadge(schedule.status)"></span>
                </td>
                <td class="col-action">
                  <button type="button" class="btn-view" @click="viewSchedule(schedule.id)" title="View details">
                    <i class="fa-solid fa-play"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="no-schedules-modal">
          <i class="fas fa-calendar-check"></i>
          <p class="text-muted">No schedules for this day.</p>
        </div>
      </div>

      <template #footer>
        <button type="button" class="btn btn-outline-secondary px-4" @click="closeDayModal">
          Close
        </button>
      </template>
    </FormModal>
  </div>
</template>

<script>
import FormModal from '@/components/utils/FormModal.vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { getStatusBadge } from '@/utils/getStatusBadge'
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'ScheduleCalendar',
  components: {
    FormModal,
    FullCalendar
  },
  data() {
    return {
      loading: false,
      calendarRef: null,
      selectedDaySchedules: [],
      selectedDayDate: null,
      currentMonth: '',
      statusColors: {
        'started': '#3b82f6',
        'under_action': '#f59e0b',
        'under_verification': '#8b5cf6',
        'completed': '#10b981',
        'on_hold': '#ef4444',
        'pending': '#6b7280',
        'rejected': '#dc2626',
        'in_process': '#3b82f6',
        'default': '#6b7280'
      },
      schedules: [],
      calendarEvents: []
    }
  },
  computed: {
    modalTitle() {
      if (!this.selectedDayDate) return 'Schedules'
      return this.selectedDayDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        initialDate: this.currentMonth ? this.currentMonth + '-01' : new Date().toISOString().slice(0, 7) + '-01',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: ''
        },
        buttonText: {
          today: 'Today',
          month: 'Month'
        },
        events: this.calendarEvents,
        eventClick: this.handleEventClick,
        dateClick: this.handleDateClick,
        moreLinkClick: this.handleMoreLinkClick,
        eventDidMount: this.handleEventDidMount,
        eventContent: this.renderEventContent,
        height: 'auto',
        contentHeight: 'auto',
        aspectRatio: 1.5,
        dayMaxEvents: 3,
        moreLinkText: (num) => `+${num} more`,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        },
        datesSet: this.handleDatesSet,
        loading: this.handleLoading,
        eventDisplay: 'block',
        displayEventTime: true
      }
    }
  },
  methods: {
    getStatusBadge,

    handleLoading(isLoading) {
      this.loading = isLoading
    },

    parseScheduleDate(dateStr) {
      if (!dateStr) return null
      
      try {
        const parts = dateStr.split(' ')
        const datePart = parts[0]
        const timePart = parts[1] + ' ' + parts[2]
        
        const dateParts = datePart.split('-')
        const day = parseInt(dateParts[0])
        const monthStr = dateParts[1]
        const year = '20' + dateParts[2]
        
        const months = {
          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        }
        const month = months[monthStr]
        if (month === undefined) return null
        
        const timeParts = timePart.split(' ')
        const timeStr = timeParts[0]
        const ampm = timeParts[1]
        
        let hours = parseInt(timeStr.split(':')[0])
        const minutes = parseInt(timeStr.split(':')[1])
        
        if (isNaN(hours) || isNaN(minutes)) return null
        
        if (ampm && ampm.toLowerCase() === 'pm' && hours < 12) hours += 12
        if (ampm && ampm.toLowerCase() === 'am' && hours === 12) hours = 0
        
        const date = new Date(parseInt(year), month, day, hours, minutes)
        return isNaN(date.getTime()) ? null : date
      } catch (e) {
        return null
      }
    },

    buildCalendarEvents() {
      if (!Array.isArray(this.schedules) || this.schedules.length === 0) {
        this.calendarEvents = []
        return
      }
      
      const events = []
      
      for (const schedule of this.schedules) {
        if (!schedule.schedule_date) continue
        
        const eventDate = this.parseScheduleDate(schedule.schedule_date)
        if (!eventDate) continue
        
        const status = schedule.status || 'pending'
        
        events.push({
          id: String(schedule.id),
          title: schedule.get_parent?.get_work_order?.name || 'Untitled',
          start: eventDate.toISOString(),
          status: status,
          backgroundColor: this.getEventColor(status),
          borderColor: this.getEventColor(status),
          textColor: '#ffffff',
          extendedProps: {
            status: status,
            work_order: schedule.get_parent?.get_work_order,
            schedule_date: schedule.schedule_date,
            schedule: schedule
          }
        })
      }
      
      this.calendarEvents = events
      
      // Refresh calendar if it exists
      if (this.calendarRef) {
        const calendar = this.calendarRef.getApi()
        calendar.refetchEvents()
      }
    },

    async initiate(month) {
      this.loading = true
      
      try {
        const payload = {
          month: month
        }

        const result = await form_api_submit({
          url: "wo/schedule/assign-to-me-calender",
          method: "GET",
          data: payload,
          show_success_toast: false,
          show_error_toast: true,
          remove_pre_toasts: false,
        })

        if (result.success) {
          this.schedules = Array.isArray(result.data) ? result.data : []
          this.buildCalendarEvents()
        } else {
          this.schedules = []
          this.calendarEvents = []
        }
      } catch (error) {
        console.error('Error fetching schedules:', error)
        this.schedules = []
        this.calendarEvents = []
      } finally {
        this.loading = false
      }
    },

    handleDatesSet(info) {
      const currentDate = info.view.currentStart
      const month = String(currentDate.getFullYear()) + '-' + String(currentDate.getMonth() + 1).padStart(2, '0')
      
      if (this.currentMonth !== month && month) {
        this.currentMonth = month
        this.initiate(month)
      }
    },

    renderEventContent(arg) {
      const dotColor = this.getEventColor(arg.event.extendedProps.status || 'pending')
      const timeText = arg.timeText || ''
      const wrapper = document.createElement('div')
      wrapper.className = 'custom-event-pill'
      wrapper.innerHTML = `
        <span class="event-dot" style="background:${dotColor}"></span>
        ${timeText ? `<span class="event-time">${timeText}</span>` : ''}
        <span class="event-title">${arg.event.title}</span>
      `
      return { domNodes: [wrapper] }
    },

    getEventColor(status) {
      return this.statusColors[status] || this.statusColors.default
    },

    getStatusClass(status) {
      const classes = {
        'started': 'status-started',
        'under_action': 'status-action',
        'under_verification': 'status-verification',
        'completed': 'status-completed',
        'on_hold': 'status-hold',
        'pending': 'status-pending',
        'rejected': 'status-rejected',
        'in_process': 'status-in-process'
      }
      return classes[status] || 'status-default'
    },

    formatStatus(status) {
      const statusMap = {
        'started': 'Started',
        'under_action': 'Under Action',
        'under_verification': 'Under Verification',
        'completed': 'Completed',
        'on_hold': 'On Hold',
        'pending': 'Pending',
        'rejected': 'Rejected',
        'in_process': 'In Process'
      }
      return statusMap[status] || status || 'Pending'
    },

    formatTime(dateStr) {
      if (!dateStr) return ''
      const date = this.parseScheduleDate(dateStr)
      if (!date) return ''
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getSchedulesForDay(dateInput) {
      if (!Array.isArray(this.schedules)) {
        return []
      }
      
      const target = new Date(dateInput)
      const targetDate = target.getDate()
      const targetMonth = target.getMonth()
      const targetYear = target.getFullYear()
      
      return this.schedules
        .filter(s => {
          if (!s.schedule_date) return false
          const d = this.parseScheduleDate(s.schedule_date)
          if (!d) return false
          return d.getDate() === targetDate && 
                 d.getMonth() === targetMonth && 
                 d.getFullYear() === targetYear
        })
        .sort((a, b) => {
          const dateA = this.parseScheduleDate(a.schedule_date)
          const dateB = this.parseScheduleDate(b.schedule_date)
          return dateA - dateB
        })
    },

    openDayWithSchedules(dateInput) {
      const daySchedules = this.getSchedulesForDay(dateInput)
      this.selectedDaySchedules = daySchedules
      this.selectedDayDate = new Date(dateInput)
      this.$refs.dayModal.showModal()
    },

    handleEventClick(info) {
      this.openDayWithSchedules(info.event.start)
    },

    handleDateClick(info) {
      this.openDayWithSchedules(info.date)
    },

    handleMoreLinkClick(info) {
      this.openDayWithSchedules(info.date)
      return 'none'
    },

    handleEventDidMount(info) {
      // Add tooltip or custom styling if needed
    },

    closeDayModal() {
      this.$refs.dayModal.closeModal()
    },

    goToToday() {
      if (this.calendarRef) {
        const calendar = this.calendarRef.getApi()
        calendar.today()
      }
    },

    viewSchedule(id) {
      this.$router.push({ name: 'ConductSchedule', params: { id } })


    },

    prevMonth() {
      if (this.calendarRef) {
        const calendar = this.calendarRef.getApi()
        calendar.prev()
      }
    },

    nextMonth() {
      if (this.calendarRef) {
        const calendar = this.calendarRef.getApi()
        calendar.next()
      }
    }
  },

  mounted() {
    const today = new Date()
    const currentMonth = String(today.getFullYear()) + '-' + String(today.getMonth() + 1).padStart(2, '0')
    this.currentMonth = currentMonth
    this.initiate(currentMonth)
  }
}
</script>

<style scoped>
.schedule-calendar-page {
  min-height: 100vh;
  background: #f6f7fb;
}

.page-header {
  background: white;
  padding: 20px 24px;
  border-radius: 14px;
  margin-bottom: 20px;
  border: 1px solid #ececf3;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

.page-header h3 {
  color: #1a202c;
}

.btn-today {
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 500;
  color: #64748b;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.calendar-container {
  background: white;
  border: 1px solid #ececf3;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  position: relative;
}

.calendar-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 14px;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  font-size: 15px;
  font-weight: 500;
  color: #667eea;
}

.loading-spinner i {
  font-size: 22px;
}

:deep(.fc) {
  font-family: inherit;
}

:deep(.fc .fc-toolbar-title) {
  font-size: 17px;
  font-weight: 600;
  color: #1a202c;
}

:deep(.fc .fc-button) {
  background: #ffffff;
  border: 1px solid #d1d5db;
  color: #4b5563;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-transform: capitalize;
}

:deep(.fc .fc-button:hover) {
  background: #667eea;
  border-color: #667eea;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

:deep(.fc .fc-button-primary:not(:disabled):active) {
  background: #667eea;
  border-color: #667eea;
}

:deep(.fc .fc-button-primary:focus) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
}

:deep(.fc .fc-daygrid-day) {
  min-height: 90px;
  cursor: pointer;
  transition: background 0.15s ease;
}

:deep(.fc .fc-daygrid-day:hover) {
  background: #f5f6fd;
}

:deep(.fc .fc-daygrid-day-frame) {
  padding: 2px;
}

:deep(.fc .fc-daygrid-day-number) {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  padding: 6px 8px;
}

:deep(.fc .fc-day-other .fc-daygrid-day-number) {
  color: #c3cad4;
}

:deep(.fc .fc-day-today) {
  background: #f5f6ff !important;
}

:deep(.fc .fc-day-today .fc-daygrid-day-number) {
  background: #667eea;
  color: #ffffff;
  border-radius: 50%;
  padding: 2px 6px;
  min-width: 24px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.35);
}

:deep(.fc .fc-col-header-cell) {
  padding: 10px 0;
  background: #fafbfc;
}

:deep(.fc .fc-col-header-cell-cushion) {
  font-size: 11.5px;
  font-weight: 600;
  color: #8792a2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
}

:deep(.fc .fc-daygrid-event) {
  border: none;
  background: transparent !important;
  margin: 1px 3px;
  cursor: pointer;
}

:deep(.fc .fc-daygrid-more-link) {
  font-size: 10.5px;
  color: #667eea;
  font-weight: 600;
  margin: 2px 4px;
}

:deep(.fc .fc-daygrid-more-link:hover) {
  color: #4338ca;
  text-decoration: underline;
}

:deep(.custom-event-pill) {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  padding: 3px 7px;
  border-radius: 6px;
  background: #f1f2fb;
  white-space: nowrap;
  overflow: hidden;
  transition: background 0.15s ease;
}

:deep(.custom-event-pill:hover) {
  background: #e7e9fb;
}

:deep(.event-dot) {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

:deep(.event-time) {
  font-size: 9px;
  font-weight: 500;
  color: #7c8798;
  flex-shrink: 0;
}

:deep(.event-title) {
  font-weight: 500;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Modal - Table Style */
.day-schedule-modal {
  padding: 2px 0;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-summary {
  font-size: 12px;
  font-weight: 500;
  color: #8792a2;
  margin-bottom: 12px;
}

.schedule-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.schedule-table thead th {
  text-align: left;
  padding: 8px 10px;
  font-weight: 600;
  color: #64748b;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e9ecef;
}

.schedule-table tbody td {
  padding: 9px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.schedule-table tbody tr:hover {
  background: #f8fafc;
}

.schedule-table tbody tr:last-child td {
  border-bottom: none;
}

.col-time {
  width: 100px;
  min-width: 100px;
  font-weight: 500;
  color: #64748b;
  font-size: 12px;
}

.col-time i {
  font-size: 11px;
  color: #b0b8c4;
  margin-right: 4px;
}

.col-title {
  min-width: 120px;
}

.schedule-name {
  font-weight: 500;
  color: #334155;
  font-size: 13px;
}

.schedule-id {
  font-size: 10px;
  color: #b0b8c4;
  margin-top: 1px;
}

.col-status {
  width: 150px;
  min-width: 150px;
}

.col-action {
  width: 50px;
  min-width: 50px;
  text-align: center;
}

.btn-view {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #667eea;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 12px;
}

.btn-view:hover {
  background: #667eea;
  border-color: #667eea;
  color: #fff;
}

.no-schedules-modal {
  padding: 48px 20px;
  text-align: center;
}

.no-schedules-modal i {
  font-size: 28px;
  color: #d8dee8;
  margin-bottom: 10px;
  display: block;
}

.no-schedules-modal p {
  margin: 0;
  color: #94a3b8;
  font-size: 13px;
}

/* Status colors */
.status-started { background: #dbeafe; color: #1e40af; }
.status-in-process { background: #dbeafe; color: #1e40af; }
.status-action { background: #fef3c7; color: #92400e; }
.status-verification { background: #ede4ff; color: #5b21b6; }
.status-completed { background: #d1fae5; color: #065f46; }
.status-hold { background: #fee2e2; color: #991b1b; }
.status-pending { background: #f1f5f9; color: #475569; }
.status-rejected { background: #fee2e2; color: #991b1b; }
.status-default { background: #f1f5f9; color: #475569; }

/* Responsive */
@media (max-width: 768px) {
  .schedule-calendar-page {
    padding: 12px !important;
  }

  .page-header {
    padding: 16px;
  }

  .status-legend {
    gap: 8px;
  }

  .legend-item {
    font-size: 10px;
  }

  .calendar-container {
    padding: 12px;
  }

  :deep(.fc .fc-toolbar) {
    flex-wrap: wrap;
    gap: 8px;
  }

  :deep(.fc .fc-toolbar-title) {
    font-size: 15px;
  }

  :deep(.fc .fc-button) {
    font-size: 11px;
    padding: 4px 10px;
  }

  :deep(.fc .fc-daygrid-day) {
    min-height: 60px;
  }

  :deep(.fc .fc-daygrid-day-number) {
    font-size: 11px;
  }

  :deep(.custom-event-pill) {
    font-size: 8px;
    padding: 2px 5px;
  }

  .schedule-table {
    font-size: 12px;
  }

  .schedule-table thead th {
    font-size: 10px;
    padding: 6px 8px;
  }

  .schedule-table tbody td {
    padding: 5px 8px;
  }

  .col-time {
    width: 80px;
    min-width: 80px;
    font-size: 11px;
  }

  .col-status {
    width: 100px;
    min-width: 100px;
  }

  .schedule-name {
    font-size: 12px;
  }

  .btn-view {
    width: 24px;
    height: 24px;
    font-size: 10px;
  }

  .loading-spinner {
    font-size: 13px;
    padding: 12px 18px;
  }

  .loading-spinner i {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  :deep(.fc .fc-daygrid-day) {
    min-height: 50px;
  }

  :deep(.fc .fc-daygrid-day-number) {
    font-size: 10px;
    padding: 2px 3px;
  }

  :deep(.custom-event-pill) {
    font-size: 7px;
    padding: 1px 4px;
  }

  :deep(.fc .fc-toolbar-title) {
    font-size: 13px;
  }

  :deep(.fc .fc-button) {
    font-size: 10px;
    padding: 3px 8px;
  }

  .page-header h3 {
    font-size: 18px;
  }

  .status-legend {
    display: none;
  }

  .schedule-table {
    font-size: 11px;
  }

  .schedule-table thead th {
    font-size: 9px;
    padding: 4px 6px;
  }

  .schedule-table tbody td {
    padding: 4px 6px;
  }

  .col-time {
    width: 60px;
    min-width: 60px;
    font-size: 10px;
  }

  .col-status {
    width: 80px;
    min-width: 80px;
  }

  .col-action {
    width: 40px;
    min-width: 40px;
  }

  .schedule-name {
    font-size: 11px;
  }

  .btn-view {
    width: 20px;
    height: 20px;
    font-size: 9px;
  }

  .loading-spinner {
    font-size: 11px;
    padding: 10px 14px;
  }

  .loading-spinner i {
    font-size: 16px;
  }
}
</style>